import { Alert, AlertTitle, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

type Severity = "info" | "error" | "success" | "warning"

type StyleProps = Readonly<{ fullWidth: boolean }>

const useStyles = makeStyles<StyleProps>()((theme, { fullWidth }) => ({
    alert: {
        width: fullWidth ? "100%" : "fit-content",
    },
    alertTitle: {
        margin: 0,
    },

    alertInfo: {
        background: theme.palette.tailwind.sky[50],
        borderRadius: ".5rem",
    },
    alertTitleInfo: {
        color: theme.palette.tailwind.sky[700],
        fontWeight: "normal",
    },

    alertError: {
        background: theme.palette.tailwind.red[600],
        borderRadius: ".25rem",
        "& .MuiAlert-icon": {
            color: theme.palette.common.white,
        },
        "& .MuiAlert-message": {
            color: theme.palette.common.white,
        },
    },
    alertTitleError: {
        color: theme.palette.common.white,
        fontWeight: "normal",
    },

    alertSuccess: {
        background: theme.palette.tailwind.green[100],
        borderRadius: ".5rem",
        "& .MuiAlert-icon": {
            color: theme.palette.tailwind.green[700],
        },
    },
    alertTitleSuccess: {
        color: theme.palette.tailwind.green[700],
        fontWeight: "normal",
    },

    alertWarning: {
        background: theme.palette.tailwind.amber[50],
        border: `1px solid ${theme.palette.tailwind.amber[200]}`,
        borderRadius: ".75rem",
        "& .MuiAlert-icon": {
            color: theme.palette.tailwind.amber[800],
        },
    },
    alertTitleWarning: {
        color: theme.palette.tailwind.slate[800],
        fontWeight: 700,
    },

    // body2 sets its own weight; the title takes the severity's weight from AlertTitle instead
    titleText: {
        fontWeight: "inherit",
    },
    description: {
        color: theme.palette.tailwind.slate[600],
    },
}))

type StandardAlertInfoProps = Readonly<{
    title: string
    /** Optional second line under the title, in a muted colour. */
    description?: string
    icon?: boolean
    className?: string
    severity?: Severity
    useDefaultStyles?: boolean
    /** Stretch to the container instead of hugging the text. */
    fullWidth?: boolean
    onClose?: () => void
}>

export function StandardAlertInfo({
    title,
    description,
    icon,
    className,
    severity = "info",
    useDefaultStyles = false,
    fullWidth = false,
    onClose,
}: StandardAlertInfoProps) {
    const { classes, cx } = useStyles({ fullWidth })

    const alertClasses: Record<Severity, string> = {
        info: classes.alertInfo,
        error: classes.alertError,
        success: classes.alertSuccess,
        warning: classes.alertWarning,
    }

    const titleClasses: Record<Severity, string> = {
        info: classes.alertTitleInfo,
        error: classes.alertTitleError,
        success: classes.alertTitleSuccess,
        warning: classes.alertTitleWarning,
    }

    const alertClass = useDefaultStyles ? undefined : alertClasses[severity]
    const titleClass = useDefaultStyles ? undefined : titleClasses[severity]

    return (
        <Alert severity={severity} icon={icon} onClose={onClose} className={cx(classes.alert, alertClass, className)}>
            <AlertTitle className={cx(classes.alertTitle, titleClass)}>
                <Typography variant="body2" className={cx(titleClass && classes.titleText)}>
                    {title}
                </Typography>
            </AlertTitle>
            {description && (
                <Typography variant="body2" className={classes.description}>
                    {description}
                </Typography>
            )}
        </Alert>
    )
}
